/**
 * Field: the one text or number input, with its label and an optional
 * description or error under it. React Aria wires the label, the
 * description and the error to the input for assistive tech; the
 * palette paints the field in both modes.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  FieldError,
  Input,
  Label,
  NumberField,
  Text,
  TextArea,
  TextField,
  type InputRenderProps,
  type NumberFieldProps,
  type TextFieldProps,
} from 'react-aria-components';
import { BRAND, CONTROL, DIM, FONT, RADIUS, SPACE, TEXT, WALK, WASH_ALPHA, WEIGHT, tint, usePalette, type Palette } from '../theme';

type Common = {
  label: ReactNode;
  /** Hide the label visually (the placeholder or context names it). */
  labelHidden?: boolean;
  description?: ReactNode;
  errorMessage?: ReactNode;
  placeholder?: string;
  size?: 'sm' | 'md';
  /** Digits line up: the input renders in the mono face with tabular numerals. */
  mono?: boolean;
  /** An answer box rather than a line: the taller field a person writes words in. */
  multiline?: boolean;
  /** The kind walk's own fields (Figma 00): the leaf's edit box under its small label, the new box's words on a raised ground, the surfaced draft's one word. */
  look?: FieldLook;
};

export type FieldLook = 'standard' | 'leaf' | 'raised' | 'word';

/** What a walk look changes on the input itself: the box's own height, ground, padding and size. */
function lookStyle(p: Palette, look: FieldLook): CSSProperties {
  switch (look) {
    case 'standard':
      return {};
    case 'leaf':
      return { height: WALK.editRows, padding: `${SPACE.sm}px ${CONTROL.padXCompact}px`, fontSize: TEXT.control, resize: 'none' };
    case 'raised':
      return {
        height: WALK.words,
        padding: `${CONTROL.padYCompact}px ${CONTROL.padXSm}px`,
        fontSize: TEXT.caption,
        background: p.surfaceRaised,
        border: 'none',
        resize: 'none',
        lineHeight: 'normal',
      };
    case 'word':
      return { height: 'auto', padding: `${SPACE.sm}px ${CONTROL.padXCompact}px`, fontSize: TEXT.control, width: WALK.wordWidth };
  }
}

/** The label of a leaf's edit box: small, bold and muted, as the frame sets it. */
function walkLabelStyle(p: Palette, look: FieldLook, hidden: boolean): CSSProperties {
  return look === 'leaf' && !hidden ? { fontSize: TEXT.micro, fontWeight: WEIGHT.strong, color: p.inkMuted } : fieldLabelStyle(p, hidden);
}

export const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function fieldLabelStyle(p: Palette, hidden: boolean): CSSProperties {
  return hidden ? VISUALLY_HIDDEN : { fontSize: TEXT.caption, color: p.inkSecondary };
}

export function inputStyle(
  p: Palette,
  size: 'sm' | 'md',
  mono: boolean,
  s: InputRenderProps & { isInvalid?: boolean },
): CSSProperties {
  return {
    height: size === 'sm' ? CONTROL.heightSm : CONTROL.height,
    padding: `0 ${size === 'sm' ? CONTROL.padXSm : CONTROL.padX}px`,
    borderRadius: RADIUS.md,
    border: `1px solid ${s.isInvalid ? p.bad : s.isFocused ? BRAND.teal : s.isHovered ? p.inkMuted : p.hairline}`,
    background: p.surface,
    color: p.ink,
    font: 'inherit',
    fontFamily: mono ? FONT.mono : FONT.sans,
    fontVariantNumeric: mono ? 'tabular-nums' : undefined,
    fontSize: size === 'sm' ? TEXT.control : TEXT.body,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: s.isFocused ? `0 0 0 3px ${tint(BRAND.teal, WASH_ALPHA.focusRing)}` : undefined,
    opacity: s.isDisabled ? DIM.disabled : 1,
  };
}

/** The answer box: a field several lines tall, set at the control size. */
export function areaStyle(p: Palette, s: InputRenderProps & { isInvalid?: boolean }): CSSProperties {
  return {
    ...inputStyle(p, 'sm', false, s),
    height: CONTROL.area,
    padding: `${SPACE.sm}px ${CONTROL.padXCompact}px`,
    resize: 'none',
    lineHeight: 'normal',
  };
}

const STACK: CSSProperties = { display: 'flex', flexDirection: 'column', gap: SPACE.xs, minWidth: 0 };

export function Field({
  label,
  labelHidden = false,
  description,
  errorMessage,
  placeholder,
  size = 'md',
  mono = false,
  multiline = false,
  look = 'standard',
  ...rest
}: Omit<TextFieldProps, 'className' | 'style' | 'children'> & Common) {
  const p = usePalette();
  return (
    <TextField {...rest} style={STACK}>
      {({ isInvalid }) => (
        <>
          <Label style={walkLabelStyle(p, look, labelHidden)}>{label}</Label>
          {multiline ? (
            <TextArea placeholder={placeholder} style={(s) => ({ ...areaStyle(p, { ...s, isInvalid }), ...lookStyle(p, look) })} />
          ) : (
            <Input placeholder={placeholder} style={(s) => ({ ...inputStyle(p, size, mono, { ...s, isInvalid }), ...lookStyle(p, look) })} />
          )}
          {description && (
            <Text slot="description" style={{ fontSize: TEXT.caption, color: p.inkMuted }}>
              {description}
            </Text>
          )}
          <FieldError style={{ fontSize: TEXT.caption, color: p.bad }}>{errorMessage}</FieldError>
        </>
      )}
    </TextField>
  );
}

export function NumberInput({
  label,
  labelHidden = false,
  description,
  errorMessage,
  placeholder,
  size = 'md',
  ...rest
}: Omit<NumberFieldProps, 'className' | 'style' | 'children'> & Omit<Common, 'mono'>) {
  const p = usePalette();
  return (
    <NumberField {...rest} style={STACK}>
      {({ isInvalid }) => (
        <>
          <Label style={fieldLabelStyle(p, labelHidden)}>{label}</Label>
          <Input placeholder={placeholder} style={(s) => inputStyle(p, size, true, { ...s, isInvalid })} />
          {description && (
            <Text slot="description" style={{ fontSize: TEXT.caption, color: p.inkMuted }}>
              {description}
            </Text>
          )}
          <FieldError style={{ fontSize: TEXT.caption, color: p.bad }}>{errorMessage}</FieldError>
        </>
      )}
    </NumberField>
  );
}
